import { AssetStatus, TenantStatusCopyType } from "@hydrologiq/gql-experience/graphql"
import PaginatedFigureCard from "@hydrologiq/shared/figures/generic/PaginatedFigureCard"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import { isCompactFigure } from "@hydrologiq/shared/figures/generic/figureVariant"
import { ElectrolyserStatusValue } from "./electrolyserStatusVisuals"
import { useCopyStatuses } from "@hydrologiq/shared/providers/helpers/useCopy"

export interface ElectrolyserSummary {
  id: string
  name: string
  /** Asset status enum value, or null when the backend sent nothing recognisable. */
  status: AssetStatus | null
  flowRate: number | null
}

interface ElectrolyserStatusFigureProps {
  /** List of electrolysers (from collectElectrolysers) */
  electrolysers: ElectrolyserSummary[]
  loading?: boolean
  compact?: boolean
}

/**
 * Overview card "Electrolyser Status"
 * ONE card for all electrolysers, using PaginatedFigureCard to tab between them.
 * 
 * Rendered from the system-view nodes (see systems `collectElectrolysers`). 
 * Shows unit's:
 * - status and, 
 * - when filling, fill rate.
 */
function ElectrolyserStatusFigure({
  electrolysers,
  loading,
  compact = isCompactFigure("summary"),
}: ElectrolyserStatusFigureProps) {
  // Same label source as StatusBadge, so a tenant that renames a status sees it renamed here too.
  const statusCopy = useCopyStatuses(TenantStatusCopyType.Asset)

  return (
    <PaginatedFigureCard
      heading="Electrolyser Status"
      loading={loading}
      compact={compact}
      items={electrolysers}
      emptyState={
        <FigureContent.Root>
          <FigureContent.SubText>No electrolysers</FigureContent.SubText>
          <FigureContent.Value>—</FigureContent.Value>
        </FigureContent.Root>
      }
      renderItem={(el) => {
        const isFilling = el.flowRate != null && el.flowRate > 0
        return (
          <FigureContent.Root>
            <FigureContent.SubText>
              {el.name} {isFilling ? `(Filling — ${el.flowRate} NL/h)` : ""}
            </FigureContent.SubText>
            <ElectrolyserStatusValue status={el.status} statusCopy={statusCopy} />
          </FigureContent.Root>
        )
      }}
    />
  )
}

export default ElectrolyserStatusFigure
